// src/Components/Resident/ResidentApprovals.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Spinner, Table } from "react-bootstrap";
import {
  collection,
  onSnapshot,
  query,
  updateDoc,
  where,
  doc,
  serverTimestamp,
  orderBy,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../rbac/AuthContext";

function beep() {
  // WebAudio beep to avoid autoplay restrictions (triggered in response to realtime, may still require user gesture in some browsers)
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.value = 880;
    o.connect(g);
    g.connect(ctx.destination);
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
    o.start();
    setTimeout(() => {
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.2);
      o.stop(ctx.currentTime + 0.2);
    }, 180);
  } catch {
    /* no-op */
  }
}

export default function ResidentApprovals({ communityId }) {
  const { currentUser } = useAuth();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const firstLoadRef = useRef(true);
  const knownIdsRef = useRef(new Set());

  useEffect(() => {
    if (!currentUser?.uid) return;
    setLoading(true);
    const qv = query(
      collection(db, "visits"),
      where("communityId", "==", communityId),
      where("residentUid", "==", currentUser.uid),
      orderBy("requestedAt", "desc"),
    );
    const unsub = onSnapshot(
      qv,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPending(rows);

        // play a beep for any brand new "pending"
        if (!firstLoadRef.current) {
          for (const v of rows) {
            if (v.status === "pending" && !knownIdsRef.current.has(v.id)) {
              beep();
            }
          }
        }
        knownIdsRef.current = new Set(rows.map((r) => r.id));
        firstLoadRef.current = false;
        setLoading(false);
      },
      (e) => {
        console.error(e);
        setErr(e?.message || "Failed to load approvals.");
        setLoading(false);
      },
    );
    return () => unsub();
  }, [communityId, currentUser?.uid]);

  const setStatus = async (v, status) => {
    try {
      const patch = { status };
      if (status === "approved") patch.approvedAt = serverTimestamp();
      if (status === "denied") patch.deniedAt = serverTimestamp();
      await updateDoc(doc(db, "visits", v.id), patch);
    } catch (e) {
      alert(e?.message || "Failed to update status.");
    }
  };

  return (
    <Card>
      <Card.Header>
        <strong>Approvals</strong>
      </Card.Header>
      <Card.Body>
        {err && <Alert variant="warning">{err}</Alert>}
        {loading ? (
          <div className="d-flex align-items-center">
            <Spinner animation="border" size="sm" className="me-2" />
            <span>Loading…</span>
          </div>
        ) : (
          <Table responsive size="sm" bordered hover>
            <thead>
              <tr>
                <th>Visitor</th>
                <th>Flat</th>
                <th>Category</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pending.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center">
                    No requests yet
                  </td>
                </tr>
              ) : (
                pending.map((v) => (
                  <tr key={v.id}>
                    <td>
                      <div className="fw-semibold">{v.visitorName}</div>
                      <div className="text-muted">
                        <small>{v.phone || "—"}</small>
                      </div>
                    </td>
                    <td>
                      <code>{v.flatLabel}</code>
                    </td>
                    <td className="text-capitalize">{v.category}</td>
                    <td>
                      <Badge bg="warning" className="text-uppercase">
                        {v.status}
                      </Badge>
                    </td>
                    <td className="d-flex gap-2 flex-wrap">
                      {v.status === "pending" && (
                        <>
                          <Button
                            size="sm"
                            variant="success"
                            onClick={() => setStatus(v, "approved")}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => setStatus(v, "denied")}
                          >
                            Deny
                          </Button>
                        </>
                      )}
                      {v.status !== "pending" && (
                        <span className="text-muted">No action</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
        <div className="text-muted">
          <small>
            Make sure your browser tab is active; some browsers block audio
            unless interacted with the page once.
          </small>
        </div>
      </Card.Body>
    </Card>
  );
}
